/**
 * UnifiedInboxTable — the departmental message list.
 *
 * Composes the shared primitives: `BulkBar` for selection, `MailRow` for every
 * row, with the unified entry from `surfaceContract` supplying the grid, the
 * column set and the action set. Before the revamp was wired this file drew its
 * own rows, which is how the two inboxes ended up with two different read-state
 * treatments over the same `is_read` field.
 *
 * Two cells here carry real logic and are passed to MailRow as renderers rather
 * than pushed into it: the Recipient cell and the reply badge. The read
 * endpoints collapse per-mailbox copies of one message into a single logical
 * entry carrying `recipients[]`, so a lead emailed to alice@ and CC'd to bob@ is
 * ONE row listing both — not two rows the admin has to notice are the same
 * message. That belongs to this surface, not to every row everywhere.
 *
 * What this file still owns: the sortable column header, the scroll container,
 * the infinite-scroll sentinel, and arrow-key roving focus — all list concerns,
 * none of them row concerns.
 */
import React, { useEffect, useRef } from 'react';
import { formatInZone, parseInstant } from '../../lib/timezone';
import useDisplayZone from '../../hooks/useDisplayZone';
import { Loader2, Mail, Reply, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { HoverCard, HoverCardTrigger, HoverCardContent } from '../ui/hover-card';
import BulkBar from './BulkBar';
import MailRow from './MailRow';
import { SURFACES, rowActionsFor, bulkActionsFor } from './surfaceContract';
import { draftPayload } from './draftRows';
import { buildThreadNav } from './threadNav';
import { readerSearch } from './inboxDeepLink';
import { elapsedLabel } from './inboxRules';
import { InboxSkeleton } from './inboxUtils';

const COLUMNS = [
    { key: 'recipient_display_name', label: 'Recipient', className: 'w-56' },
    { key: 'sender_email', label: 'Sender', className: 'w-56 hidden md:flex' },
    { key: 'subject', label: 'Subject', className: 'flex-1' },
    { key: 'received_at', label: 'Received', className: 'w-24' },
    { key: 'replied', label: 'Replied?', className: 'w-28' },
];

// #532: labels render in the viewer's display zone (profile → browser); the
// day bucketing is computed in the same zone so "Yesterday" agrees with the
// label next to it. Offset-less strings are UTC instants (parseInstant).
function formatReceived(value, now = Date.now(), zone) {
    const date = parseInstant(value);
    if (!date) return '';
    const dayKey = (d) => formatInZone(d, { zone, formatStr: 'yyyy-MM-dd' });
    const thatDay = dayKey(date);
    if (thatDay === dayKey(now)) return formatInZone(date, { zone, formatStr: 'hh:mm a zzz' });
    if (thatDay === dayKey(now - 86400000)) return 'Yesterday';
    const days = Math.floor((now - date.getTime()) / 86400000);
    if (days < 7) return formatInZone(date, { zone, formatStr: 'EEE' });
    return formatInZone(date, { zone, formatStr: 'MMM d' });
}

function RecipientAvatar({ mail, size = 'sm' }) {
    const initial = (mail || '?').charAt(0).toUpperCase();
    const sizes = {
        sm: 'w-5 h-5 text-[10px]',
        md: 'w-6 h-6 text-xs',
    };
    return (
        <span className={`${sizes[size]} rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold flex-shrink-0`}>
            {initial}
        </span>
    );
}

function RecipientCell({ entry, isUnified }) {
    // `recipient_display` is resolved server-side: the member mailboxes that
    // received a copy, falling back to the original's envelope recipients.
    //
    // Horizontal single-line layout: avatar + email for first 2 recipients,
    // "+N" overflow indicator, and a Radix HoverCard showing all recipients
    // on hover. This fixes the overlap bug and matches the Gmail/Outlook UX.
    const mails = entry.recipient_display || [];
    const MAX_SHOWN = 2;
    const shown = mails.slice(0, MAX_SHOWN);
    const overflow = mails.length - shown.length;

    if (!isUnified) {
        return (
            <span className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm text-stone-700 truncate">{mails[0] || '—'}</span>
            </span>
        );
    }

    if (!mails.length) {
        return <span className="text-sm text-stone-400">—</span>;
    }

    return (
        <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
                <span
                    className="flex items-center gap-1 min-w-0 cursor-default"
                    data-testid={`recipient-cell-${entry.comm_id}`}
                >
                    {shown.map(mail => (
                        <span
                            key={mail}
                            className="inline-flex items-center gap-1 min-w-0"
                            data-testid={`recipient-chip-${entry.comm_id}-${mail}`}
                        >
                            <RecipientAvatar mail={mail} size="sm" />
                            <span className="text-xs text-stone-600 truncate max-w-[80px]">{mail}</span>
                        </span>
                    ))}
                    {overflow > 0 && (
                        <span
                            className="inline-flex items-center gap-0.5 text-xs text-stone-500 bg-stone-100 px-1 py-0.5 rounded flex-shrink-0"
                            data-testid={`recipient-overflow-${entry.comm_id}`}
                        >
                            +{overflow}
                        </span>
                    )}
                </span>
            </HoverCardTrigger>
            <HoverCardContent className="w-72">
                <div className="space-y-1.5">
                    <p className="text-xs font-medium text-stone-500 mb-1">
                        Recipients ({mails.length})
                    </p>
                    {mails.map(mail => (
                        <div key={mail} className="flex items-center gap-2">
                            <RecipientAvatar mail={mail} size="sm" />
                            <span className="text-sm text-stone-700 truncate">{mail}</span>
                        </div>
                    ))}
                </div>
            </HoverCardContent>
        </HoverCard>
    );
}

function ReplyStatusBadge({ entry }) {
    const displayZone = useDisplayZone();
    // `reply_state` is derived per-conversation on every read: "awaiting" when
    // the newest inbound message has no answer after it, "replied" when it does,
    // "none" for a thread we started that nobody has written into.
    //
    // It replaces `Boolean(entry.replied_at)`, which was sticky — reply credit
    // was copied forward from any message in the thread, so once a conversation
    // had ever been answered it could never show Awaiting again, even with a
    // newer unanswered message sitting on top of it.
    const state = entry.reply_state
        || (entry.replied_at ? 'replied' : 'awaiting');  // pre-upgrade payloads

    if (state === 'none' || entry.no_reply_needed) {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded" data-testid="replied-n/a">
                <Mail className="w-3.5 h-3.5" />
                N/A
            </span>
        );
    }

    const replied = state === 'replied';
    const answeredAt = entry.answered_at || entry.replied_at;

    if (replied && entry.replied_from_personal) {
        const time = formatReceived(answeredAt, Date.now(), displayZone);
        return (
            <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded"
                  data-testid="replied-personal"
                  title={`Replied from personal address ${entry.replied_as || ''}`}>
                <Reply className="w-3.5 h-3.5" />
                Replied{time ? ` · ${time}` : ''}
            </span>
        );
    }

    if (replied) {
        const time = formatReceived(answeredAt, Date.now(), displayZone);
        return (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded"
                  data-testid="replied-yes"
                  title={`Replied${entry.replied_as ? ` as ${entry.replied_as}` : ''}`}>
                <Reply className="w-3.5 h-3.5" />
                Replied{time ? ` · ${time}` : ''}
            </span>
        );
    }

    // Elapsed since the message we owe a reply to — NOT since the last activity.
    // `comm_date` is now the newest message in the thread, which includes our own
    // sends, so using it restarted the SLA clock every time we replied: the
    // opposite of what an "awaiting reply" timer means.
    const awaitingSince = entry.awaiting_since || entry.comm_date;
    const elapsed = awaitingSince ? elapsedLabel(new Date(awaitingSince)) : '';
    return (
        <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded"
              data-testid="replied-no">
            <XCircle className="w-3.5 h-3.5" />
            Awaiting{elapsed ? ` · ${elapsed}` : ''}
        </span>
    );
}


/** One header cell per grid column, so each label sits over the cell it names.
 *
 * `surface.columnLabels` is already one entry per grid column — index 0 is the
 * checkbox and index 1 the star, both unlabelled — so the mapping is direct.
 * The labels stay buttons because sorting is real here and predates the revamp:
 * the design puts sort in a control-strip popover that does not exist yet, and
 * removing a working control is a capability loss rather than a refinement.
 */
function headerCells({ sortKey, sortDirection, onSort }) {
    const byLabel = new Map(COLUMNS.map(c => [c.label.toLowerCase(), c]));
    return SURFACES.unified.columnLabels.map((label) => {
        if (!label) return null;
        const column = byLabel.get(label.toLowerCase());
        if (!column) return label;
        return (
            <button
                type="button"
                onClick={() => onSort?.(column.key)}
                data-testid={`sort-${column.key}`}
                aria-sort={
                    sortKey === column.key
                        ? sortDirection === 'asc' ? 'ascending' : 'descending'
                        : 'none'
                }
                aria-label={`Sort by ${column.label}`}
                className="text-left truncate hover:text-stone-700 transition-colors"
            >
                {label}
                {sortKey === column.key && (
                    <span aria-hidden="true">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                )}
            </button>
        );
    });
}


export default function UnifiedInboxTable({
    entries = [],
    sortKey,
    sortDirection = 'desc',
    onSort,
    canDelete = false,
    emptyState,
    inboxId,
    isUnified = false,
    hasMore = false,
    /** The SERVER's match count, for the reader's "1 of N". */
    totalCount,
    onLoadMore,
    isLoadingMore = false,
    isLoading = false,
    /** A `useMailSelection` return value — the single source of selection. */
    selection,
    /** `(actionKey, mail) => void` for every row and bulk action. */
    onAction,
    folderLabel = 'Inbox',
    /**
     * Whether this viewer may triage at all (responder and above).
     *
     * Selection exists to drive bulk verbs, every one of which the API gates on
     * a `responder` grant — so a viewer gets no checkboxes and no bulk bar
     * rather than controls that 403.
     */
    canSelect = true,
}) {
    // Removing a message from a shared inbox is a MANAGER verb; everything
    // else here is triage, which a responder may do. Derived in
    // `surfaceContract` so the row strip and the bulk bar cannot disagree
    // about who may delete — and so Trash swaps both strips for the bin's own
    // two doors rather than growing a second, divergent rule (#710).
    const rowActions = rowActionsFor(SURFACES.unified, {
        folder: folderLabel,
        canDelete,
    });
    // Selection has to lead to at least one legal action, or it leads to
    // controls that 403: in Trash without the manager grant the resolver
    // returns nothing, and a checkbox column above an empty action strip is
    // worse than no checkboxes. Everywhere else a responder may still triage,
    // so `canSelect` alone is the floor.
    const effectiveCanSelect = canSelect
        && (folderLabel !== 'trash' || canDelete);
    const bulkActions = bulkActionsFor(SURFACES.unified, {
        folder: folderLabel,
        canSelect: effectiveCanSelect,
        canDelete,
        selection,
    });
    const displayZone = useDisplayZone();
    const navigate = useNavigate();
    const rowRefs = useRef([]);
    const sentinelRef = useRef(null);
    const scrollRef = useRef(null);
    const observerRef = useRef(null);

    // --- IntersectionObserver for infinite scroll ---
    //
    // Declared BEFORE the early returns below, because a hook that runs only on
    // some renders is the one rule React does not bend: the loading and empty
    // branches return early, and putting this after them would change the hook
    // order the moment the first page arrived.
    //
    // `root` is the scroll container, not the viewport. This list scrolls inside
    // its own box, so the page itself never moves and a viewport-rooted sentinel
    // would sit below the fold forever without intersecting — the same reasoning
    // PersonalInboxTable documents.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        const root = scrollRef.current;
        if (!sentinel) return undefined;

        observerRef.current?.disconnect();
        if (!hasMore || isLoadingMore) return undefined;

        observerRef.current = new IntersectionObserver(
            (observed) => {
                if (observed[0]?.isIntersecting && hasMore && !isLoadingMore && onLoadMore) {
                    onLoadMore();
                }
            },
            {
                root: root || null,
                // Start loading 300px before the sentinel is on screen so the
                // next page is usually there by the time the user reaches it.
                rootMargin: '0px 0px 300px 0px',
                threshold: 0,
            }
        );

        observerRef.current.observe(sentinel);
        return () => observerRef.current?.disconnect();
    }, [hasMore, isLoadingMore, onLoadMore, entries.length]);

    if (isLoading && entries.length === 0) {
        return (
            <div className="flex-1 overflow-y-auto" aria-busy="true">
                <InboxSkeleton />
            </div>
        );
    }

    if (entries.length === 0) {
        return <div className="flex-1 overflow-y-auto">{emptyState}</div>;
    }

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {effectiveCanSelect && (
            <BulkBar
                selection={selection}
                folderLabel={folderLabel}
                gridTemplate={SURFACES.unified.gridTemplate}
                columnCells={headerCells({ sortKey, sortDirection, onSort })}
                onAction={(key) => onAction?.(key, null, { source: 'bulk' })}
                actions={bulkActions}
            />
            )}
        <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto min-h-0"
            data-testid="unified-inbox-table"
            aria-busy={isLoading}
        >
            <div>
                {entries.map((entry, index) => (
                    <MailRow
                        key={entry.comm_id}
                        mail={entry}
                        surface={SURFACES.unified}
                        rowRef={(el) => { rowRefs.current[index] = el; }}
                        rowActions={rowActions}
                        folder={folderLabel}
                        selectable={canSelect}
                        selected={selection.selectedIds.has(entry.comm_id)}
                        onSelect={selection.toggleOne}
                        onOpen={(mail) => {
                            // A draft has no thread to read — it has never been
                            // sent, and it is not in `communications` at all, so
                            // the thread route below resolves nothing and the
                            // reader opens on a 404. Clicking one reopens the
                            // composer, which is the only way a draft gets back
                            // into compose. The personal lane has always done
                            // this; this lane lists drafts through the same
                            // endpoint and did not.
                            if (mail?.is_draft) {
                                navigate(`/inbox/${inboxId}/compose`, {
                                    state: { draft: draftPayload(mail) },
                                });
                                return;
                            }
                            // The same identity the URL uses, so `buildThreadNav`
                            // finds this row and ‹ › resolve to real routes.
                            const idOf = (m) => m?.conversation_id || m?.comm_id;
                            // A Trash row is un-tagged from this inbox, so only
                            // the reader's Trash read can open it; the live
                            // thread would 404, or show what is left in the inbox.
                            // Any other folder is handed over too, so the reader
                            // can lead back to it when there is no history entry.
                            const query = readerSearch({
                                inTrash: folderLabel === 'trash',
                                listFolder: folderLabel,
                            });
                            navigate(`/inbox/${inboxId}/${idOf(mail)}${query}`, {
                                state: {
                                    threadNav: buildThreadNav(
                                        entries, idOf(mail), totalCount, idOf
                                    ),
                                },
                            });
                        }}
                        onAction={onAction}
                        onKeyDown={(e) => {
                            // Roving focus between rows. It lives here because
                            // this is the only thing that knows there ARE other
                            // rows — MailRow handles Enter/Space and nothing else.
                            if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                rowRefs.current[index + 1]?.focus();
                            } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                rowRefs.current[index - 1]?.focus();
                            }
                        }}
                        renderOwnerCell={(mail) => (
                            <RecipientCell entry={mail} isUnified={isUnified} />
                        )}
                        renderMetaExtra={(mail) => <ReplyStatusBadge entry={mail} />}
                    />
                ))}
            </div>
            {hasMore && (
                <div
                    ref={sentinelRef}
                    className="py-6 flex justify-center border-t border-stone-100"
                    data-testid="inbox-scroll-sentinel"
                >
                    {isLoadingMore && (
                        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                    )}
                </div>
            )}
            {!hasMore && entries.length > 0 && (
                <div
                    className="flex justify-center py-3 text-xs text-stone-400 border-t border-stone-100"
                    data-testid="inbox-all-loaded"
                >
                    All messages loaded
                </div>
            )}
        </div>
        </div>
    );
}

export function InboxEmptyState({ title, body, action, icon: Icon = Mail }) {
    return (
        <div
            className="flex flex-col items-center justify-center h-64 text-center px-6"
            data-testid="inbox-empty-state"
        >
            <Icon className="w-12 h-12 mb-3 text-stone-300" />
            <p className="font-medium text-stone-600">{title}</p>
            {body && <p className="text-sm mt-1 text-stone-400 max-w-md">{body}</p>}
            {action && <div className="mt-4">{action}</div>}
        </div>
    );
}
